  if (!customElements.get('store-header')) {
    customElements.define('store-header', class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.headerResizeObserver?.disconnect();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        const { focus, motion } = window.ThemeComponents;
        const sectionWrapper = this.closest('.shopify-section');
        const isTransparent = this.classList.contains('store-header--transparent');
        const isSticky = this.classList.contains('store-header--sticky');
        const stickyStart = sectionWrapper ? sectionWrapper.getBoundingClientRect().top + window.scrollY : 0;

        const updateHeaderPosition = () => {
          let hasScrolled = false;
          if (isTransparent) {
            hasScrolled = document.body.classList.contains('product-variant-preview-active') || window.scrollY > stickyStart;
            this.classList.toggle('store-header--scrolled', hasScrolled);
            if (isSticky) sectionWrapper?.classList.toggle('is-stuck', hasScrolled);
          }
          const viewportBottom = Math.max(0, this.getBoundingClientRect().bottom);
          const renderedHeight = this.querySelector('.store-header__bar')?.getBoundingClientRect().height;
          if (renderedHeight) document.documentElement.style.setProperty('--header-rendered-height', `${renderedHeight}px`);
          const contentOffset = isTransparent && !hasScrolled ? 0 : viewportBottom;
          this.style.setProperty('--desktop-header-offset', `${viewportBottom}px`);
          this.style.setProperty('--mobile-header-offset', `${viewportBottom}px`);
          document.documentElement.style.setProperty('--site-header-offset', `${contentOffset}px`);
        };

        updateHeaderPosition();
        const headerBar = this.querySelector('.store-header__bar');
        if ('ResizeObserver' in window && headerBar) {
          this.headerResizeObserver = new ResizeObserver(updateHeaderPosition);
          this.headerResizeObserver.observe(headerBar);
        }
        window.addEventListener('scroll', updateHeaderPosition, { passive: true, signal });
        window.addEventListener('resize', updateHeaderPosition, { passive: true, signal });
        window.addEventListener('product-gallery:preview-change', updateHeaderPosition, { signal });

        const megaClosers = [];
        this.querySelectorAll('.desktop-mega, .desktop-menu').forEach(mega => {
          const summary = mega.querySelector(':scope > summary');
          const panel = mega.querySelector('.desktop-mega__panel, .desktop-menu__panel');
          let animation;

          const openMega = () => {
            megaClosers.forEach(({ element, close }) => { if (element !== mega) close(true); });
            animation?.cancel();
            if (!mega.open) mega.open = true;
            animation = motion.play(
              panel,
              [
                { opacity: .3, transform: 'translateY(-2px) scale(.9995)' },
                { opacity: 1, transform: 'translateY(0) scale(1)' }
              ],
              { duration: 160, easing: 'cubic-bezier(.2, .8, .2, 1)' }
            );
          };

          const closeMega = (immediate = false) => {
            if (!mega.open) return;
            animation?.cancel();
            if (immediate || motion.reduced) {
              mega.open = false;
              return;
            }
            animation = motion.play(
              panel,
              [
                { opacity: 1, transform: 'translateY(0) scale(1)' },
                { opacity: 0, transform: 'translateY(-1px) scale(.9995)' }
              ],
              { duration: 110, easing: 'cubic-bezier(.4, 0, 1, 1)' }
            );
            const closingAnimation = animation;
            closingAnimation.finished.then(() => {
              if (animation === closingAnimation) mega.open = false;
            }).catch(() => {});
          };
          megaClosers.push({ element: mega, close: closeMega });

          summary.addEventListener('click', event => {
            if (!matchMedia('(min-width: 1101px)').matches) return;
            event.preventDefault();
            if (!mega.open) openMega();
          }, { signal });
          mega.addEventListener('mouseenter', () => {
            if (!matchMedia('(min-width: 1101px)').matches) return;
            openMega();
          }, { signal });
          mega.addEventListener('mouseleave', event => {
            if (!matchMedia('(min-width: 1101px)').matches) return;
            if (this.querySelector('.store-header__bar')?.contains(event.relatedTarget)) return;
            closeMega();
          }, { signal });
          window.addEventListener('scroll', () => {
            if (!mega.open || !matchMedia('(min-width: 1101px)').matches) return;
            closeMega();
          }, { passive: true, signal });
        });
        this.querySelectorAll('.desktop-nav > a').forEach(link => {
          const closeOpenMegas = () => {
            if (matchMedia('(min-width: 1101px)').matches) {
              megaClosers.forEach(({ close }) => close(true));
            }
          };
          link.addEventListener('mouseenter', closeOpenMegas, { signal });
          link.addEventListener('focus', closeOpenMegas, { signal });
        });
        this.addEventListener('mouseleave', () => megaClosers.forEach(({ close }) => close()), { signal });

        const mobileNav = this.querySelector('.mobile-nav');
        const mobileToggle = this.querySelector('.mobile-nav__toggle');
        this.querySelector('[data-mobile-nav-close]')?.addEventListener('click', () => {
          mobileNav.open = false;
          mobileToggle?.focus({ preventScroll: true });
        }, { signal });
        mobileNav?.addEventListener('toggle', () => {
          mobileToggle?.setAttribute('aria-label', mobileNav.open ? mobileNav.dataset.closeLabel : mobileNav.dataset.openLabel);
          if (mobileNav.open && matchMedia('(max-width: 1100px)').matches) {
            focus.first(this.querySelector('.mobile-nav__links'));
          } else {
            this.querySelectorAll('.mobile-submenu[open]').forEach(submenu => { submenu.open = false; });
          }
        }, { signal });

        const submenuClosers = new WeakMap();
        const localizationClosers = new WeakMap();
        this.querySelectorAll('.mobile-submenu').forEach(submenu => {
          const panel = submenu.querySelector('.mobile-submenu__panel');
          const summary = submenu.querySelector(':scope > summary');
          let animation;

          const closeSubmenu = () => {
            if (!submenu.open) return;
            animation?.cancel();
            if (motion.reduced || !panel) {
              submenu.open = false;
              focus.restore(summary);
              return;
            }
            animation = motion.play(
              panel,
              [
                { opacity: 1, transform: 'translateX(0)' },
                { opacity: 0, transform: 'translateX(100%)' }
              ],
              { duration: 140, easing: 'cubic-bezier(.4, 0, 1, 1)' }
            );
            const closingAnimation = animation;
            closingAnimation.finished.then(() => {
              if (animation !== closingAnimation) return;
              submenu.open = false;
              focus.restore(summary);
            }).catch(() => {});
          };

          submenuClosers.set(submenu, closeSubmenu);
          submenu.addEventListener('toggle', () => {
            if (submenu.open && matchMedia('(max-width: 1100px)').matches) {
              animation?.cancel();
              focus.first(panel);
            }
          }, { signal });
          submenu.querySelector('[data-submenu-close]')?.addEventListener('click', closeSubmenu, { signal });
        });

        this.querySelectorAll('[data-localization-select]').forEach(select => {
          select.addEventListener('change', () => select.form?.submit(), { signal });
        });

        this.querySelectorAll('.localization-selector').forEach(selector => {
          const panel = selector.querySelector('.localization-selector__panel');
          const summary = selector.querySelector(':scope > summary');
          let animation;

          const closeLocalization = () => {
            if (!selector.open) return;
            animation?.cancel();
            if (motion.reduced || !panel) {
              selector.open = false;
              return;
            }
            animation = motion.play(
              panel,
              [
                { opacity: 1, transform: 'translateY(0) scale(1)' },
                { opacity: 0, transform: 'translateY(-4px) scale(.985)' }
              ],
              { duration: 120, easing: 'cubic-bezier(.4, 0, 1, 1)' }
            );
            const closingAnimation = animation;
            closingAnimation.finished.then(() => {
              if (animation === closingAnimation) selector.open = false;
            }).catch(() => {});
          };

          localizationClosers.set(selector, closeLocalization);
          selector.addEventListener('toggle', () => {
            if (selector.open) animation?.cancel();
          }, { signal });
          document.addEventListener('pointerdown', event => {
            if (selector.open && !selector.contains(event.target)) closeLocalization();
          }, { signal });
          summary.addEventListener('click', event => {
            if (!selector.open) return;
            event.preventDefault();
            closeLocalization();
          }, { signal });
        });

        this.addEventListener('keydown', event => {
          if (event.key === 'Escape') {
            const openDetails = [...this.querySelectorAll('details[open]')];
            const lastOpen = openDetails.at(-1);
            if (!lastOpen) return;
            const closeSubmenu = submenuClosers.get(lastOpen);
            if (closeSubmenu) {
              closeSubmenu();
              return;
            }
            const closeLocalization = localizationClosers.get(lastOpen);
            if (closeLocalization) {
              closeLocalization();
              focus.restore(lastOpen.querySelector(':scope > summary'));
              return;
            }
            lastOpen.open = false;
            focus.restore(lastOpen.querySelector(':scope > summary'));
            return;
          }

          if (event.key !== 'Tab' || !mobileNav?.open || !matchMedia('(max-width: 1100px)').matches) return;
          const activePanel = this.querySelector('.mobile-submenu[open] .mobile-submenu__panel') || this.querySelector('.mobile-nav__panel');
          focus.trapTab(event, activePanel);
        }, { signal });
      }
      disconnectedCallback() {
        this.abortController?.abort();
        this.headerResizeObserver?.disconnect();
      }
    });
  }
